import { AxiosError, AxiosResponse } from "axios"
import { arrayToString, getArrayDiff, getObjectArrayDiff, isArrayOfArray, isObjectArray } from "./fns"
import _ from 'lodash'

export const extractError = (err: any): string => {
    if (typeof err === 'string') return err
    if (typeof err === 'object') {
        if ('detail' in err) return err.detail
        if ('nonFieldErrors' in err) return err.nonFieldErrors.join(', ')
    }
    return 'Unknown Error'
}

export const isFieldError = (err: any): boolean =>
    typeof err === 'object' && !('detail' in err || 'nonFieldErrors' in err)


export const getFieldErrors = (data: any, error: any, parent?: string): { [key: string]: string } => {
    let errors: { [key: string]: string } = {}
    if (typeof data === 'object' && typeof error === 'object') {
        let dataKeys = Object.keys(data)
        Object.entries(error).forEach(entry => {
            const [key, value] = entry;
            if (typeof value === 'object' && !Array.isArray(value))
                errors = { ...errors, ...getFieldErrors(data[key], value, parent ? `${parent}.${key}` : key) }
            else if (dataKeys.indexOf(key) !== -1)
                errors[parent ? `${parent}.${key}` : key] = arrayToString(value)
        })
    }
    return errors
}

type StringIndexed = { [key: string]: any }

const emptyStringToNull = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(emptyStringToNull)
    if (obj && typeof obj === 'object') {
        const res: StringIndexed = {}
        Object.entries(obj as StringIndexed).forEach(([k, v]) => {
            res[k] = (typeof v === 'string' && v === '') ? null : emptyStringToNull(v)
        })
        return res
    }
    return obj
}

export const getEditedPayload = <T extends StringIndexed>(
    initialValues: T, data: T, ignoreKeys: string[] = [], fullObjectKeys: string[] = []
): Partial<T> => {
    if (!initialValues) return data
    let result: StringIndexed = {}
    Object.entries(data as StringIndexed).forEach(([key, value]) => {
        if (ignoreKeys.indexOf(key) !== -1)
            return

        // Normalize empty strings to undefined for both new value and initial value
        const initialVal = initialValues ? initialValues[key] : undefined
        const val = (typeof value === 'string' && value === '') ? undefined : value
        const orig = (typeof initialVal === 'string' && initialVal === '') ? undefined : initialVal

        if (val == null) {
            if (val !== orig)
                result[key] = val
        }
        else if (val || val === 0 || typeof val === 'boolean') {
            if (Array.isArray(val) && !isArrayOfArray(val)) {
                if (isObjectArray(val)) {
                    if (initialValues[key]) {
                        let objArrayDiff = getObjectArrayDiff(val, initialValues[key])
                        if (objArrayDiff.length > 0)
                            result[key] = objArrayDiff
                    }
                    else result[key] = val
                } else {
                    if (initialValues[key]) {
                        let arrayDiff = getArrayDiff(val, initialValues[key])
                        if (arrayDiff.length > 0)
                            result[key] = arrayDiff
                    } else result[key] = val
                }
            } else if (val instanceof File) {
                result[key] = val
            } else if (typeof val === 'object') {
                // If caller requested full object for this key, send the full updated object when any sub-key changed
                if (fullObjectKeys.indexOf(key) !== -1) {
                    let obj = getEditedPayload(initialValues[key], value)
                    if (Object.keys(obj).length > 0) {
                        result[key] = emptyStringToNull(value)
                    }
                } else {
                    let obj = getEditedPayload(initialValues[key], value)
                    if (Object.keys(obj).length > 0)
                        result[key] = obj
                }
            } else if ((typeof val === "string" || typeof val === "number" || typeof val === 'boolean') &&
                orig !== val) {
                result[key] = val
            }
        }
    })
    return result as Partial<T>
}

const linkHeaderRegex = /<([^>]+)>;\s+rel="([^"]+)"/

export const extractHeaderLink = (linkHeader: string) => {
    let result: { [key: string]: string } = {}
    linkHeader.split(',')
        .forEach(link => {
            let match = link.match(linkHeaderRegex)
            if (match)
                result[match[2]] = match[1]
        })
    return result
}

export const extractAxiosHeaderLink = <T>(response: AxiosResponse<T>, linkType: 'next' | 'prev' | 'current'): string => {
    if (response.headers['link']) {
        let links = extractHeaderLink(response.headers['link'])
        if (linkType in links) return links[linkType]
    }
    return ''
}

export const objectToFormData = <T>(data: T, convert: boolean = true) => {
    const formData = new FormData()
    Object.entries(data as StringIndexed).forEach(([key, value]) => {
        if (convert) {
            key = _.snakeCase(key)
        }
        if (typeof value === 'string' || value instanceof File) {
            formData.append(key, value)
        }
        else if (typeof value === 'number') {
            formData.append(key, value.toString())
        }
    })
    return formData
}

export const getAxiosError = (error: AxiosError): string => {
    if (error.response) {
        const err = error.response.data ?? ""
        if(typeof err === 'string') return err
        if(typeof err === 'object') return Object.keys(err).map(k=> `${k}: ${err[k]}`).join(', ')
        
        return typeof err
    } else if (error.request) {
        return error.request
    } else {
        return error.message
    }
}